import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useSaleSelection } from '../hooks/useSaleSelection';
import SaleContextBanner from '../components/admin/SaleContextBanner';

interface Invoice {
  invoiceNumber: string;
  auctionId: string;
  auctionTitle: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  items: Array<{
    lotNumber: string;
    sku: string;
    title: string;
    quantity: number;
    highBid: number;
  }>;
  subtotal: number;
  buyersPremium: number;
  buyersPremiumRate: number;
  salesTax: number;
  salesTaxRate: number;
  total: number;
  status: 'unpaid' | 'paid' | 'partial' | 'picked_up';
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
  createdAt: string;
  paidAt?: string;
  paymentLink: string;
}

const InvoiceList: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const navigate = useNavigate();
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  /*
   * Fourth copy of loadAuctions(), retired. Here the route param normally wins,
   * so the createdAt-desc fallback rarely fired — but it was the same wrong key
   * waiting for the day someone linked here without one, and it made a fourth
   * place to keep in step. The hook takes the route id as the highest-precedence
   * input, so behaviour under /auctions/:auctionId/invoices is unchanged.
   */
  const selection = useSaleSelection({
    role: 'fulfillment',
    storageKey: 'invoiceList',
    routeAuctionId: auctionId,
  });
  const { saleId: selectedAuction, loading: salesLoading } = selection;
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'unpaid' | 'paid'>('all');
  const [pickFilter, setPickFilter] = useState<'all' | 'to_be_picked' | 'picked'>('to_be_picked');
  
  // Sorting - load from localStorage
  const [sortBy, setSortBy] = useState<'bidderNumber' | 'lastName' | 'total' | 'items'>(
    (localStorage.getItem('invoiceListSortBy') as any) || 'bidderNumber'
  );

  const [stats, setStats] = useState({
    total: 0,
    totalRevenue: 0,
    unpaid: 0,
    paid: 0,
    totalItems: 0,
    toBePicked: 0,
    picked: 0
  });

  useEffect(() => {
    if (selectedAuction) {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAuction]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allInvoices, paymentFilter, pickFilter, sortBy]);

  const loadInvoices = async () => {
    if (!selectedAuction) return;

    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'invoices'));
      const invoiceList: Invoice[] = [];
      
      snapshot.forEach(doc => {
        const invoice = doc.data() as Invoice;
        if (invoice.auctionId === selectedAuction) {
          invoiceList.push(invoice);
        }
      });

      setAllInvoices(invoiceList);
      calculateStats(invoiceList);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (invoices: Invoice[]) => {
    const stats = {
      total: invoices.length,
      totalRevenue: invoices.reduce((sum, inv) => sum + inv.total, 0),
      unpaid: invoices.filter(inv => inv.status === 'unpaid').length,
      paid: invoices.filter(inv => inv.status === 'paid' || inv.paidAt).length,
      totalItems: invoices.reduce((sum, inv) => sum + inv.items.length, 0),
      toBePicked: invoices.filter(inv => !inv.pickStatus || inv.pickStatus === 'pending').length,
      picked: invoices.filter(inv => inv.pickStatus === 'picked' || inv.pickStatus === 'ready_for_pickup').length
    };
    setStats(stats);
  };

  const applyFilters = () => {
    let filtered = [...allInvoices];

    // Payment filter
    if (paymentFilter === 'unpaid') {
      filtered = filtered.filter(inv => inv.status === 'unpaid');
    } else if (paymentFilter === 'paid') {
      filtered = filtered.filter(inv => inv.status === 'paid' || inv.paidAt);
    }

    // Pick filter
    if (pickFilter === 'to_be_picked') {
      filtered = filtered.filter(inv => !inv.pickStatus || inv.pickStatus === 'pending');
    } else if (pickFilter === 'picked') {
      filtered = filtered.filter(inv => inv.pickStatus === 'picked' || inv.pickStatus === 'ready_for_pickup');
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'bidderNumber':
          return parseInt(a.bidderNumber) - parseInt(b.bidderNumber);
        case 'lastName':
          return a.bidder.lastName.localeCompare(b.bidder.lastName);
        case 'total':
          return b.total - a.total;
        case 'items':
          return b.items.length - a.items.length;
        default:
          return 0;
      }
    });

    setFilteredInvoices(filtered);
  };

  const handleSortChange = (newSort: typeof sortBy) => {
    setSortBy(newSort);
    localStorage.setItem('invoiceListSortBy', newSort);
  };

  // Route-driven page: the URL is the selection, so switching means navigating.
  // Writing a pin here would be beaten by the route id on the next render.
  const handleAuctionChange = useCallback((newAuctionId: string) => {
    navigate(`/auctions/${newAuctionId}/invoices`);
  }, [navigate]);

  const handlePrintAll = () => {
    window.print();
  };

  if (salesLoading || loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Invoices</h1>
        </div>
        <button
          onClick={handlePrintAll}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Print All
        </button>
      </div>

      {/* Sale in view, selector, and the stale-sale warning. print:hidden — the
          printed invoice list is a document, not a console. */}
      <SaleContextBanner selection={selection} label="Invoices" onSelect={handleAuctionChange} className="print:hidden" />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Revenue</div>
          <div className="text-2xl font-bold text-green-600">
            ${stats.totalRevenue.toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Unpaid / Paid</div>
          <div className="text-2xl font-bold">
            <span className="text-red-600">{stats.unpaid}</span>
            <span className="text-gray-400"> / </span>
            <span className="text-green-600">{stats.paid}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">To Pick / Picked</div>
          <div className="text-2xl font-bold">
            <span className="text-orange-600">{stats.toBePicked}</span>
            <span className="text-gray-400"> / </span>
            <span className="text-blue-600">{stats.picked}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Items</div>
          <div className="text-2xl font-bold text-gray-900">{stats.totalItems}</div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Payment Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Payment Status:
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setPaymentFilter('all')}
                className={`flex-1 px-3 py-2 rounded ${
                  paymentFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPaymentFilter('unpaid')}
                className={`flex-1 px-3 py-2 rounded ${
                  paymentFilter === 'unpaid'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Unpaid
              </button>
              <button
                onClick={() => setPaymentFilter('paid')}
                className={`flex-1 px-3 py-2 rounded ${
                  paymentFilter === 'paid'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Paid
              </button>
            </div>
          </div>

          {/* Pick Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pick Status:
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setPickFilter('all')}
                className={`flex-1 px-3 py-2 rounded ${
                  pickFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPickFilter('to_be_picked')}
                className={`flex-1 px-3 py-2 rounded ${
                  pickFilter === 'to_be_picked'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                To Pick
              </button>
              <button
                onClick={() => setPickFilter('picked')}
                className={`flex-1 px-3 py-2 rounded ${
                  pickFilter === 'picked'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Picked
              </button>
            </div>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sort By:
            </label>
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value as typeof sortBy)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="bidderNumber">Bidder Number</option>
              <option value="lastName">Last Name</option>
              <option value="total">Total (High to Low)</option>
              <option value="items">Item Count</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          Showing {filteredInvoices.length} of {stats.total} invoices
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bidder #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredInvoices.map((invoice) => (
              <tr
                key={invoice.invoiceNumber}
                onClick={() => navigate(`/invoices/${invoice.invoiceNumber}`)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">
                    #{invoice.bidderNumber}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                    {invoice.bidder.lastName}, {invoice.bidder.firstName}
                  </div>
                  {/* Raw doc id demoted to a truncated, tooltip'd line so it stays available for
                      support without eating the row (was its own full-width column). */}
                  <div
                    className="text-[10px] font-mono text-gray-400 truncate max-w-[160px]"
                    title={invoice.invoiceNumber}
                  >
                    {invoice.invoiceNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>{invoice.bidder.email}</div>
                  <div>{invoice.bidder.phone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                  {invoice.items.length}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-green-600">
                  ${invoice.total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="space-y-1">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' || invoice.paidAt
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {invoice.status === 'paid' || invoice.paidAt ? 'Paid' : 'Unpaid'}
                    </span>
                    {invoice.pickStatus && (
                      <span
                        className={`block px-2 py-1 text-xs font-semibold rounded-full ${
                          invoice.pickStatus === 'ready_for_pickup'
                            ? 'bg-green-100 text-green-800'
                            : invoice.pickStatus === 'picked'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {invoice.pickStatus === 'ready_for_pickup'
                          ? 'Ready'
                          : invoice.pickStatus === 'picked'
                          ? 'Picked'
                          : 'Pending'}
                      </span>
                    )}
                  </div>
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Link
                    to={`/invoices/${invoice.invoiceNumber}`}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </Link>
                  <button
                    onClick={() => window.open(`/invoices/${invoice.invoiceNumber}/print`, '_blank')}
                    className="text-gray-600 hover:text-gray-900 mr-3"
                  >
                    Print
                  </button>
                  <Link
                    to={`/picker/order/${invoice.invoiceNumber}`}
                    className={`font-semibold ${
                      invoice.pickStatus === 'picked' || invoice.pickStatus === 'ready_for_pickup'
                        ? 'text-gray-600 hover:text-gray-900'
                        : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {invoice.pickStatus === 'picked' || invoice.pickStatus === 'ready_for_pickup'
                      ? '👁️ View Order'
                      : '📦 Pick Order'}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredInvoices.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No invoices match the selected filters
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceList;